<template>
  <div class="hero">
    <div class="wrap hero-wrap">
      <div>
        <span class="eyebrow">{{ eyebrow }}</span>
        <h1>
          Welcome, {{ user.first_name }} — your
          {{ course.name.split(' — ')[0] }} journey starts soon.
        </h1>
        <p>
          You're all set. Here's everything you need before Day 1 — your
          checklist, what to prepare, and where to get help along the way.
        </p>
        <div class="cta">
          <button type="button" class="btn btn-primary" @click="toProcess">
            View your checklist →
          </button>
          <button
            type="button"
            class="btn btn-ghost hero-ghost"
            @click="$emit('practical')"
          >
            Practical information
          </button>
        </div>
      </div>
      <div class="badge">
        <div class="seal">
          <b>{{ daysLeft }}<i>days left</i></b>
        </div>
        <div class="bt">{{ untilLabel }}</div>
        <div class="bs">{{ course.course_label }}</div>
        <a href="#process" @click.prevent="toProcess">
          View your checklist →
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LearningHubHero',
  props: {
    course: {
      type: Object,
      required: true
    }
  },
  computed: {
    user () {
      return this.$store.state.user
    },
    eyebrow () {
      return 'The ' + this.course.name.replace(' — ', ' · ')
    },
    firstSession () {
      return this.course.sessions
        .slice()
        .sort((a, b) => (a.start_dt < b.start_dt ? -1 : 1))[0]
    },
    daysLeft () {
      if (!this.firstSession) return 0
      const start = new Date(this.firstSession.start_dt.replace(' ', 'T'))
      const now = new Date()
      return Math.max(0, Math.ceil((start - now) / 86400000))
    },
    untilLabel () {
      if (!this.firstSession) return ''
      const prefix = this.firstSession.title.split(' — ')[0]
      const date = new Date(this.firstSession.start_dt.replace(' ', 'T'))
      const label = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      })
      return `Until ${prefix.replace(/\(Day[^)]*\)/, `(${label})`)}`
    }
  },
  methods: {
    toProcess () {
      this.$emit('process')
      const el = document.getElementById('process')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>

<style scoped>
.hero {
  background: linear-gradient(160deg, var(--teal-dk), var(--ink));
  color: var(--paper);
  padding: 40px 0 44px;
}
.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 24px;
}
.hero-wrap {
  display: grid;
  grid-template-columns: 1.4fr 0.6fr;
  gap: 30px;
  align-items: center;
}
.eyebrow {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #8fd2dc;
}
h1 {
  font-size: clamp(1.9rem, 4vw, 2.9rem);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: #fff;
  margin: 8px 0 10px;
}
.hero p {
  color: rgba(255, 255, 255, 0.85);
  max-width: 52ch;
}
.cta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.94rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease;
  font-family: inherit;
}
.btn:hover {
  transform: translateY(-1px);
}
.btn-primary {
  background: var(--teal);
  color: #fff;
}
.btn-ghost {
  background: transparent;
  border-color: var(--line);
  color: var(--ink);
}
.hero-ghost {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}
.badge {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 18px;
  padding: 22px;
  text-align: center;
}
.seal {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  margin: 0 auto 12px;
  background: conic-gradient(
    from 210deg,
    #8fd2dc,
    #007a8f,
    #00343a,
    #8fd2dc
  );
  display: grid;
  place-items: center;
}
.seal b {
  background: var(--ink);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8fd2dc;
  font-size: 1.35rem;
  line-height: 1;
}
.seal b i {
  display: block;
  font-size: 0.5rem;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c9e8ec;
  margin-top: 3px;
}
.bt {
  font-weight: 700;
  color: #fff;
  font-size: 0.95rem;
}
.bs {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}
.badge a {
  display: inline-block;
  margin-top: 12px;
  font-size: 0.82rem;
  color: #8fd2dc;
  font-weight: 600;
  text-decoration: none;
}
@media (max-width: 800px) {
  .hero-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
